import React, { useMemo } from 'react';
import { Plus, X, Clock, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '../../ui/button';
import type { AppConfig, Segment } from '@/lib/types';

interface ScriptMarkerProps {
  config: AppConfig;
  currentTime: number;
  onAddSegment: (text: string, endTime: number) => void;
  onDeleteSegment: (id: string) => void;
}

export function ScriptMarker({ config, currentTime, onAddSegment, onDeleteSegment }: ScriptMarkerProps) {
  const allLines = useMemo(() => config.text.split('\n'), [config.text]);
  
  const lineMarkers = useMemo(() => {
    const linesWithMarkers = new Map<number, Segment>();
    let lineIdx = 0;
    config.segments.forEach(segment => {
      const segmentLines = segment.text.split('\n');
      const markerLineIdx = lineIdx + segmentLines.length - 1;
      linesWithMarkers.set(markerLineIdx, segment);
      lineIdx += segmentLines.length;
    });
    return linesWithMarkers;
  }, [allLines, config.segments]);

  const handleMark = (idx: number) => {
    let lastMarkedIdx = -1;
    for (let i = idx - 1; i >= 0; i--) {
      if (lineMarkers.has(i)) {
        lastMarkedIdx = i;
        break;
      }
    }

    const lastEndTime = lastMarkedIdx >= 0 ? lineMarkers.get(lastMarkedIdx)!.endTime : 0;
    if (currentTime <= lastEndTime) return; 

    const text = allLines.slice(lastMarkedIdx + 1, idx + 1).join('\n');
    onAddSegment(text, currentTime);
  };

  const formatTime = (time: number) => {
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);
    const ms = Math.floor((time % 1) * 10);
    return `${m}:${s.toString().padStart(2, '0')}.${ms}`;
  };

  return (
    <div className="relative flex flex-col h-full bg-background/20 rounded-3xl border-x border-b border-white/5 overflow-hidden">
      {/* Floating Time Display */}
      <div className="absolute top-6 right-8 z-20 flex items-center gap-2 px-4 py-2 rounded-2xl bg-primary text-primary-foreground shadow-2xl shadow-primary/20 backdrop-blur-xl border border-white/10 select-none">
        <Clock className="size-3.5 fill-current/20" />
        <span className="text-xs font-mono font-black tracking-wider">{formatTime(currentTime)}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-3 custom-scrollbar">
        {allLines.map((line, i) => {
          const isEmpty = line.trim() === '';
          const marker = lineMarkers.get(i);
          const isMarked = !!marker;

          if (isEmpty) {
            return (
              <div key={i} className="h-6 flex items-center group/empty">
                <div className="w-12 h-px bg-white/5" />
                <div className="flex-1 h-px bg-linear-to-r from-white/5 to-transparent" />
              </div>
            );
          }

          return (
            <div 
              key={i}
              className={cn(
                "group relative flex items-start gap-6 p-5 rounded-2xl transition-all duration-300",
                isMarked 
                  ? "bg-primary/5 ring-1 ring-primary/10 shadow-sm" 
                  : "hover:bg-white/5 border border-transparent hover:border-white/5"
              )}
            >
              <div className="pt-2 min-w-10 text-center">
                <span className="text-[10px] font-mono font-bold opacity-20 group-hover:opacity-40">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </div>

              <div className="flex-1 min-w-0 pt-1">
                <p className={cn(
                  "text-lg leading-relaxed transition-all duration-300",
                  isMarked ? "text-primary font-medium" : "text-foreground/80 group-hover:text-foreground"
                )}>
                  {line}
                </p>
              </div>

              <div className="flex items-center self-center gap-3">
                {isMarked ? (
                  <div className="flex items-center gap-2 animate-in zoom-in-95 duration-200">
                    <div className="flex items-center gap-2 bg-white/5 text-primary px-3 py-1.5 rounded-xl border border-primary/20">
                      <Clock className="size-3" />
                      <span className="text-[10px] font-mono font-bold">{formatTime(marker.endTime)}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 rounded-full hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-all"
                      onClick={() => onDeleteSegment(marker.id)}
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 px-5 rounded-xl opacity-0 group-hover:opacity-100 bg-white/5 hover:bg-primary hover:text-primary-foreground transition-all duration-200 border border-white/5"
                    onClick={() => handleMark(i)}
                  >
                    <Plus className="size-4 mr-2" />
                    <span className="text-[10px] font-bold uppercase tracking-wider">Sync End</span>
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {config.text.trim() === "" && (
          <div className="h-full flex flex-col items-center justify-center text-center opacity-20 py-20">
            <AlertCircle className="size-12 mb-4" />
            <p className="text-sm font-medium">No script content to sync.<br />Add text in Classic Mode first.</p>
          </div>
        )}
      </div>
    </div>
  );
}
